import { fmtDate } from '../../utils/datetime';
import React, { useEffect, useState } from "react";
import {
    AlertCircle, ChevronDown, ChevronLeft, ChevronRight,
    PhoneMissed, Search, Stethoscope, Building2, Users,
} from "lucide-react";
import PageLayout from "../../components/PageLayout";
import { useAuth } from "../../context/AuthContext";
import { getAllEmployees } from "../../api/employee";
import { getMissedCallReport } from "../../api/report";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Employee { id: number; name: string; employee_code?: string; }

interface MissedVisit {
    visit_id: number;
    visit_type: "doctor" | "firm";
    entity_name: string;
    entity_code: string | null;
    employee_name: string;
    employee_code: string | null;
    planned_date: string;
    reason: string | null;
}

interface MissedCallData {
    month: string;
    total_missed: number;
    doctor_missed: number;
    firm_missed: number;
    missed_visits: MissedVisit[];
}

interface Props { onLogout: () => void; onNavigate: (page: string) => void; }

// ── Helpers ───────────────────────────────────────────────────────────────────

const monthLabel = (ym: string) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

const prevMonth = (ym: string) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 2);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const nextMonth = (ym: string) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const currentYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: number; color: string }) {
    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
        </div>
    );
}

function TypeBadge({ type }: { type: string }) {
    return type === "doctor"
        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"><Stethoscope className="w-3 h-3" />Doctor</span>
        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"><Building2 className="w-3 h-3" />Firm</span>;
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function MissedCallsPage({ onLogout, onNavigate }: Props) {
    const { user } = useAuth();
    const tenantId = (user as any)?.tenant_id as number;

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selectedEmp, setSelectedEmp] = useState<number | null>(null);
    const [month, setMonth] = useState(currentYM());
    const [data, setData] = useState<MissedCallData | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState<"all" | "doctor" | "firm">("all");

    useEffect(() => {
        if (!tenantId) return;
        getAllEmployees(tenantId).then((list: any[]) =>
            setEmployees((Array.isArray(list) ? list : []).map(e => ({
                id: e.id,
                name: e.user?.name ?? e.name ?? e.employee_name ?? "",
                employee_code: e.employee_code ?? e.code,
            })))
        ).catch(() => {});
    }, [tenantId]);

    const fetchReport = async (m?: string, empId?: number | null) => {
        if (!tenantId) return;
        const targetMonth = m ?? month;
        setLoading(true);
        try {
            const res = await getMissedCallReport(tenantId, targetMonth, empId !== undefined ? empId : selectedEmp);
            setData(res.data.data ?? res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // auto-load all employees' data when page first opens
    useEffect(() => { if (tenantId) fetchReport(month, null); }, [tenantId]);

    const handleMonthChange = (dir: "prev" | "next") => {
        const newMonth = dir === "prev" ? prevMonth(month) : nextMonth(month);
        setMonth(newMonth);
        fetchReport(newMonth, selectedEmp);
    };

    const visits = data?.missed_visits ?? [];
    const filtered = visits.filter(v => {
        const matchSearch = !search || v.entity_name.toLowerCase().includes(search.toLowerCase()) || v.employee_name.toLowerCase().includes(search.toLowerCase());
        const matchType = typeFilter === "all" || v.visit_type === typeFilter;
        return matchSearch && matchType;
    });

    return (
        <PageLayout onLogout={onLogout} currentPage="reports/missed-calls" onNavigate={onNavigate}>
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 rounded-lg">
                            <PhoneMissed className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Missed Calls</h1>
                            <p className="text-xs text-gray-500">Planned visits that were not completed</p>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5 space-y-5">
                    {/* Filters */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                            {/* Month Navigator */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                    <button onClick={() => handleMonthChange("prev")} className="px-3 py-2 hover:bg-gray-50 border-r border-gray-200 transition-colors">
                                        <ChevronLeft className="w-4 h-4 text-gray-500" />
                                    </button>
                                    <span className="flex-1 text-center text-sm font-medium text-gray-700 py-2">{monthLabel(month)}</span>
                                    <button onClick={() => handleMonthChange("next")} className="px-3 py-2 hover:bg-gray-50 border-l border-gray-200 transition-colors">
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                                <div className="relative">
                                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <select value={selectedEmp ?? ""} onChange={e => setSelectedEmp(e.target.value ? Number(e.target.value) : null)}
                                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent appearance-none bg-white">
                                        <option value="">All Employees</option>
                                        {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            <button onClick={() => fetchReport(month, selectedEmp)} disabled={loading}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-60 transition-colors">
                                {loading ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
                                {loading ? "Loading…" : "Search"}
                            </button>
                        </div>
                    </div>

                    {data && (
                        <>
                            {/* Stats */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <StatCard icon={PhoneMissed} label="Total Missed" value={data.total_missed} color="bg-red-500" />
                                <StatCard icon={Stethoscope} label="Doctor Missed" value={data.doctor_missed} color="bg-blue-500" />
                                <StatCard icon={Building2} label="Firm Missed" value={data.firm_missed} color="bg-purple-500" />
                            </div>

                            {/* Table */}
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
                                    <div className="relative flex-1 min-w-48">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                        <input type="text" placeholder="Search entity or employee…" value={search} onChange={e => setSearch(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent" />
                                    </div>
                                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)}
                                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white">
                                        <option value="all">All Types</option>
                                        <option value="doctor">Doctor</option>
                                        <option value="firm">Firm</option>
                                    </select>
                                    <span className="text-xs text-gray-500 font-medium">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-100">
                                                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Entity</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Planned Date</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {filtered.length === 0 ? (
                                                <tr><td colSpan={4} className="text-center py-12 text-gray-400 text-sm">
                                                    {data.total_missed === 0
                                                        ? <span className="flex flex-col items-center gap-2"><AlertCircle className="w-8 h-8 opacity-30" />No missed calls for {monthLabel(month)}</span>
                                                        : "No records match your filters"}
                                                </td></tr>
                                            ) : filtered.map(v => (
                                                <tr key={`${v.visit_type}-${v.visit_id}`} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-5 py-3.5">
                                                        <div className="flex items-center gap-2">
                                                            <TypeBadge type={v.visit_type} />
                                                            <span className="font-medium text-gray-900">{v.entity_name}</span>
                                                        </div>
                                                        {v.entity_code && <span className="text-xs text-gray-400 ml-0.5">{v.entity_code}</span>}
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        <span className="text-gray-700">{v.employee_name}</span>
                                                        {v.employee_code && <span className="block text-xs text-gray-400">{v.employee_code}</span>}
                                                    </td>
                                                    <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">
                                                        {fmtDate(v.planned_date)}
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        {v.reason
                                                            ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-50 text-red-700 text-xs border border-red-100"><AlertCircle className="w-3 h-3" />{v.reason}</span>
                                                            : <span className="text-gray-400 text-xs">—</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {!data && !loading && (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                            <PhoneMissed className="w-12 h-12 mb-3 opacity-30" />
                            <p className="text-sm">Select filters and click Search to load missed calls</p>
                        </div>
                    )}
                </div>
        </PageLayout>
    );
}
